import { Box, Card, Typography } from "@mui/material";
import { CheckCircle, AccessTime } from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Subscription } from "../../Utils/Types";
import styles from "./PurchasePromoBanner.module.css";
import BuyHoursButton from "./BuyHoursButton";
import PaySchoolButton from "./PaySchoolButton";
import RenewSubscriptionButton from "./RenewSubscriptionButton";

interface PurchasePromoBannerProps {
  subscription: (Subscription & { _id?: string; expiredDate?: string }) | null;
  activeOrders?: any[];
  clientData?: any;
}

export default function PurchasePromoBanner({
  subscription,
  activeOrders = [],
  clientData,
}: PurchasePromoBannerProps) {
  // Derived values
  const hasSubscription = !!subscription?._id;

  const daysRemaining = subscription?.subscriptionExpiresAt
    ? Math.ceil(
        (new Date(subscription.subscriptionExpiresAt).getTime() -
          new Date().getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : 0;

  const renderAmount = (amount: number) => {
    if (amount == null || isNaN(amount)) return "-";
    return (
      amount.toLocaleString("ro-RO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) +
      " " +
      Vocabulary.currency
    );
  };

  // If has active subscription
  if (hasSubscription) {
    return (
      <Card className={`${styles.bannerCard} ${styles.activeSubscription}`}>
        <Box className={styles.bannerContent}>
          <Box className={styles.mainContent}>
            <Box className={styles.topRow}>
              <Box className={styles.headerSection}>
                <Box className={`${styles.iconBox} ${styles.activeIcon}`}>
                  <CheckCircle className={styles.icon} />
                </Box>
                <Box className={styles.headerText}>
                  <Typography variant="h6" className={styles.title}>
                    {Vocabulary.activePlan}
                  </Typography>
                  <Typography variant="body2" className={styles.subtitle}>
                    {subscription.name}
                  </Typography>
                </Box>
              </Box>
              <Box className={styles.statsBox}>
                <Typography variant="body2" className={styles.daysLabel}>
                  {Vocabulary.daysRemaining}
                </Typography>
                <Typography className={styles.daysValue}>
                  {daysRemaining > 0 ? daysRemaining : "0"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box className={styles.actionSection}>
            <RenewSubscriptionButton
              currentSubscription={subscription}
              clientData={clientData}
              className={styles.actionButton}
            />
          </Box>
        </Box>
      </Card>
    );
  }

  // If no subscription - show pending orders with remaining amounts
  return (
    <Card className={`${styles.bannerCard} ${styles.noSubscription}`}>
      <Box className={styles.bannerContent}>
        <Box className={styles.mainContent}>
          <Box className={styles.headerSection}>
            <Box className={`${styles.iconBox} ${styles.noSubscriptionIcon}`}>
              <AccessTime className={styles.icon} />
            </Box>
            <Box className={styles.headerText}>
              <Typography variant="h6" className={styles.title}>
                {Vocabulary.noSubscription}
              </Typography>
              <Typography variant="body2" className={styles.subtitle}>
                {Vocabulary.noSubscriptionMessage}
              </Typography>
            </Box>
          </Box>

          {activeOrders?.length > 0 &&
            activeOrders.map((order: any) => {
              const totalAmount = Number(order?.product?.price);
              const remaining = totalAmount - (order.totalPaid || 0) || 0;
              return (
                <Box key={order._id} className={styles.orderItem}>
                  <Box className={styles.orderDetails}>
                    <Typography className={styles.productName}>
                      {order?.product?.name}
                    </Typography>
                    <Box className={styles.orderAmounts}>
                      <Typography className={styles.amountLabel}>
                        De plată:{" "}
                        <span className={styles.remainingValue}>
                          {renderAmount(remaining)}
                        </span>
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={styles.orderItemButtons}>
                    {!order?.product?.isCertificate && (
                      <BuyHoursButton order={order} clientData={clientData} />
                    )}
                    <PaySchoolButton
                      order={order}
                      amountLeftToPay={remaining}
                      clientData={clientData}
                    />
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Box>
    </Card>
  );
}
